import { Timestamp, FieldValue } from 'firebase/firestore';

import type { PlayingHandicap as PlayingHandicapResult } from 'shared/dist/utils/playingHandicaps';
export { HANDICAP_CSV_HEADERS, HANDICAP_LABELS, HANDICAP_ID_CSV_HEADERS, HANDICAP_INDEX_CSV_HEADERS, findCsvValue, isCsvHeader, normalizeCsvHeader } from 'shared/dist/constants/handicapLabels';
export { resolveDayPlayingHandicaps, resolveDayStrokeSpans, resolvePlayerTeeId, type PlayingHandicap } from 'shared/dist/utils/playingHandicaps';
import { parseGhinHandicapIndex } from 'shared/dist/utils/ghinIndex';
import {
  effectiveStrokeCount,
  netDoubleBogeyForHole,
} from 'shared/dist/utils/handicap';
import { resolveRowStrokes, type HoleScope, type StrokeSpan } from 'shared/dist/utils/holeStrokes';
export { resolveRowStrokes, type HoleScope, type StrokeSpan } from 'shared/dist/utils/holeStrokes';
import { resolveCompetitionHoles } from 'shared/dist/utils/competitionDays';

import { stripUndefined } from './utils';

import type Competition from '../types/Competition';
import type { CourseTee, CourseTeeSnapshot } from '../types/Course';
import type { Gender } from '../types/Gender';

export {
  isStrokeScore,
  isXScore,
  hasEstimatedScore,
  effectiveStrokeCount,
  calculateCourseHandicap,
  calculatePlayingHandicap,
  capCourseHandicap,
  strokesReceivedOnHole,
  netScoreForHole,
  netDoubleBogeyForHole,
  isFemale,
  getEffectiveSlope,
  getEffectiveRating,
  adjustedHandicapIndex,
  getEffectiveRating9,
  getEffectiveSlope9,
  generateHandicapPctTable,
  resolveHandicapPctForRank,
  resolveUnitAllowances,
  resolveRoundPar,
  resolveEffectiveStrokeIndex,
  firstHoleNumberOfRound,
  type TeeRatingData,
  type PlayingHandicapOptions,
} from 'shared/dist/utils/handicap';

export {
  NH_HANDICAP_INDEX,
  parseGhinHandicapIndex,
  isValidHandicapIndex,
  formatHandicapIndex,
} from 'shared/dist/utils/ghinIndex';

/** Reads a Handicap Index® an admin typed ("12.4", "+2.3"): the stored number (a plus index is
 *  negative), or null when it isn't a valid index — at most 54.0 or +10.0, one decimal place. */
export function parseEnteredHandicapIndex(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\+?\d{1,2}(\.\d)?$/.test(trimmed)) return null;
  const index = parseGhinHandicapIndex(trimmed);
  return index >= -10 && index <= 54 ? index : null;
}

export interface HoleScores {
  /** Plain net per hole: gross minus strokes — no posting cap, no game's hole cap. A picked-up
   *  hole counts its x{n} estimate; a bare X (no estimate) counts net double bogey. null = no
   *  score (NS, match sentinel). */
  netByHole: (number | null)[];
  /** Net Double Bogey ceiling per hole for GHIN posting. */
  maxByHole: number[];
  /** The row's playing handicap — see RowStrokes.playingHandicap. */
  playingHandicap: number;
}

/**
 * A player's own score row for `round`, in one pass: plain net per hole and the Net Double Bogey
 * ceiling (for score entry's posting checks), from the strokes resolveRowStrokes places for `spans`
 * (resolveRowStrokeSpans) — the same per-hole strokes the results engine and the printed scorecard
 * use. Each array is indexed by position within the round. Null when no span places strokes.
 */
export function calculateHoleScores(
  scoreByHole: number[],
  snapshot: CourseTeeSnapshot,
  gender: Gender | undefined,
  round: HoleScope,
  spans: StrokeSpan[],
): HoleScores | null {
  const row = resolveRowStrokes(snapshot, gender, round, scoreByHole.length, spans);
  if (row.playingHandicap === null) return null;
  const holes = row.scoreIndex.map((s, i) => ({ gross: scoreByHole[s], par: row.par?.[i] ?? 4, strokes: row.strokes[i] ?? null }));

  const netByHole = holes.map(({ gross, par, strokes }) => {
    if (gross === undefined || strokes === null) return null;
    const strokeCount = effectiveStrokeCount(gross, netDoubleBogeyForHole(par, strokes));
    return strokeCount === null ? null : strokeCount - strokes;
  });

  const maxByHole = holes.map(({ par, strokes }) => netDoubleBogeyForHole(par, strokes ?? 0));

  return { netByHole, maxByHole, playingHandicap: row.playingHandicap };
}

/**
 * Builds a CourseTeeSnapshot from a live CourseTee.
 * Call this in TeeSheet and EventDay mutators when snapshotting tee data.
 */
export function buildTeeSnapshot(
  tee: CourseTee,
  capturedAt: Timestamp | FieldValue,
): CourseTeeSnapshot {
  return stripUndefined({
    itemId: tee.itemId,
    name: tee.name,
    mensRating: tee.mensRating,
    mensSlope: tee.mensSlope,
    mensRatingFront9: tee.mensRatingFront9,
    mensRatingBack9: tee.mensRatingBack9,
    mensSlopeFront9: tee.mensSlopeFront9,
    mensSlopeBack9: tee.mensSlopeBack9,
    womensRating: tee.womensRating,
    womensSlope: tee.womensSlope,
    womensRatingFront9: tee.womensRatingFront9,
    womensRatingBack9: tee.womensRatingBack9,
    womensSlopeFront9: tee.womensSlopeFront9,
    womensSlopeBack9: tee.womensSlopeBack9,
    par: tee.par,
    mensStrokeIndex: tee.mensStrokeIndex,
    womensStrokeIndex: tee.womensStrokeIndex,
    capturedAt,
    usgaMaleTeeSetRatingId: tee.usgaMaleTeeSetRatingId ?? null,
    usgaFemaleTeeSetRatingId: tee.usgaFemaleTeeSetRatingId ?? null,
  });
}

/**
 * A score screen row's playing handicap: the shared value for its competition
 * (resolveDayPlayingHandicaps — the same one the scorecard and results use), keyed by the row's
 * first player (a one-score team shares one value). Null when no NET competition applies or the
 * player has no handicap data.
 */
export function resolveRowPlayingHandicap(
  playingHandicapsByCompId: Map<string, Map<string, PlayingHandicapResult>>,
  competition: Competition | undefined,
  playerIds: string[],
): number | null {
  if (!competition) return null;
  return playingHandicapsByCompId.get(competition.itemId)?.get(playerIds[0] ?? '')?.playingHandicap ?? null;
}

/**
 * A score screen row's stroke spans: a team row (one-score game) gets its team game's shared
 * playing handicap over that game's holes; anyone else their own score row's spans for the day
 * (resolveDayStrokeSpans — the same ones the printed scorecard uses). Empty when no NET game
 * applies or the player has no handicap data.
 */
export function resolveRowStrokeSpans(
  strokeSpansByPlayerId: Map<string, StrokeSpan[]>,
  playingHandicapsByCompId: Map<string, Map<string, PlayingHandicapResult>>,
  teamCompetition: Competition | undefined,
  day: Parameters<typeof resolveCompetitionHoles>[1],
  playerIds: string[],
  isTeamRow: boolean,
): StrokeSpan[] {
  if (isTeamRow && teamCompetition) {
    const ph = resolveRowPlayingHandicap(playingHandicapsByCompId, teamCompetition, playerIds);
    return ph === null ? [] : [{ ...resolveCompetitionHoles(teamCompetition, day), playingHandicap: ph }];
  }
  return strokeSpansByPlayerId.get(playerIds[0] ?? '') ?? [];
}
